import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { User, CheckSquare } from 'lucide-react';

export function EditEmployeeModal({ isOpen, onClose, employee, onUpdateEmployee, roles }) {
    const [formData, setFormData] = useState({
        name: "",
        roles: [],
    });

    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (employee) {
            // Split the comma-separated roles string into an array, trim spaces too
            const employeeRolesArray = employee.role
                ? employee.role.split(",").map(role => role.trim())
                : [];

            setFormData({
                name: employee.name || "",
                roles: employee.roles || employeeRolesArray,
            });
        }
    }, [employee]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleCheckboxChange = (role) => {
        const updatedRoles = formData.roles.includes(role)
            ? formData.roles.filter((r) => r !== role)
            : [...formData.roles, role];
        setFormData((prev) => ({ ...prev, roles: updatedRoles }));
        if (errors.roles) setErrors((prev) => ({ ...prev, roles: "" }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = "Name is required";
        if (formData.roles.length === 0) newErrors.roles = "At least one role is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (validateForm() && employee) {
            // Join roles array back to comma-separated string if needed
            const rolesString = formData.roles.join(",");

            onUpdateEmployee({
                ...employee,
                name: formData.name,
                role: rolesString,  // Send as string to backend
                roles: formData.roles, // Keep array for frontend usage if needed
            });

            setFormData({ name: "", roles: [] });
            onClose();
        }
    };

    if (!employee) return null;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-md p-0 overflow-hidden">
                <DialogHeader className="px-6 pt-6 pb-2">
                    <DialogTitle className="text-xl font-medium">Update Employee</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-4">
                    <div className="space-y-4">
                        {/* Name Input */}
                        <div className="relative">
                            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                                <User className="h-5 w-5 text-gray-500" />
                            </div>
                            <Input
                                id="name"
                                name="name"
                                placeholder="Full Name"
                                value={formData.name}
                                onChange={handleChange}
                                className={`pl-10 py-5 ${errors.name ? "border-red-500" : ""}`}
                            />
                            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                        </div>

                        {/* Multi-role Select with Checkboxes */}
                        <div className="relative">
                            <div className="absolute inset-y-0 left-3 flex items-start pt-2 pointer-events-none">
                                <CheckSquare className="h-5 w-5 text-gray-500" />
                            </div>
                            <div className="pl-10 py-4 border rounded-md">
                                <p className="text-sm font-medium text-gray-700 mb-2">Select Roles</p>
                                <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                                    {roles.map((role, index) => (
                                        <label key={index} className="flex items-center space-x-2 text-sm">
                                            <input
                                                type="checkbox"
                                                value={role}
                                                checked={formData.roles.includes(role)}
                                                onChange={() => handleCheckboxChange(role)}
                                            />
                                            <span>{role}</span>
                                        </label>
                                    ))}
                                </div>
                                {errors.roles && <p className="text-xs text-red-500 mt-2">{errors.roles}</p>}
                            </div>
                        </div>
                    </div>

                    <Button
                        type="submit"
                        className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-medium py-5"
                    >
                        Update Employee
                    </Button>
                </form>
            </DialogContent>
        </Dialog>
    );
}
